// Subscriber editor popup — lets the owner manage WHICH of their profiles a given
// person (subscriber) can see. Extracted into its own module so it can be reused
// from both the Subscribers list and the shared contact-card popup without a
// circular import. Operates on Subscription objects where the current user is the
// profile owner.
import { h, toast, confirmDialog, rowAction } from '../components/dom.js';
import { api } from '../api/client.js';
import type { Subscription, Profile } from '../api/types.js';

// ---- pure helpers (DOM-free, unit-tested) ----
// These encode the popup's two rules so they can be verified without a document:
//  - addableProfiles: profiles this person does NOT yet see (dropdown contents).
//  - canRemove: a subscription is removable only when it's NOT the last one.
export function addableProfiles(profiles: Profile[], current: Subscription[]): Profile[] {
  const seen = new Set(current.map((m) => m.profile_id));
  return profiles.filter((p) => !seen.has(p.id));
}
export function canRemove(current: Subscription[]): boolean {
  return current.length > 1;
}

// openSubscriberEditor lets the owner ADD the person to another profile (a 2nd
// subscription) or REMOVE any of their per-profile subscriptions — EXCEPT the
// last remaining one (removing their final profile, i.e. revoking them entirely,
// is only allowed from the main subscription view). There is intentionally no
// "move" affordance here; move = remove-from-one + add-to-another, which is
// confusing alongside add. `mine` is all this person's active subscriptions.
//
// The popup refreshes IN PLACE after every add/remove (it does NOT close): the
// added profile appears in the current-list and drops out of the Add dropdown;
// a removed profile disappears from the list and reappears in the dropdown; and
// once a single profile remains its Remove link vanishes. The parent `reload()`
// still fires in the background so the page/card underneath stays in sync.
export function openSubscriberEditor(s: Subscription, mine: Subscription[], profiles: Profile[], reload: () => Promise<void>): void {
  const nameOf = (id?: string) => profiles.find((p) => p.id === id)?.name || id || '—';

  // Local, mutable working copy of this person's subscriptions so the popup can
  // update without closing. Kept in sync with each successful add/remove.
  let current: Subscription[] = [...mine];

  const overlay = h('div', { style: 'position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:50;padding:20px', onclick: () => overlay.remove() });

  // Containers we re-render in place; both derive from `current` so a single
  // rerender() keeps the dropdown and the list mutually consistent.
  const addSlot = h('div', {});
  const list = h('div', {});

  // Fire the parent reload but never let a background refresh failure break the
  // popup's own in-place update (the popup already reflects the new state).
  const bgReload = (): void => { void reload().catch(() => { /* non-fatal */ }); };

  // ADD: subscribe this person to ANOTHER of my profiles (only ones not yet seen).
  const renderAdd = (): void => {
    const addable = addableProfiles(profiles, current);
    if (!addable.length) {
      addSlot.replaceChildren(h('p', { class: 'muted' }, h('small', {}, 'They already see all of your profiles.')));
      return;
    }
    const addSelect = h('select', { class: 'select' },
      ...addable.map((p) => h('option', { value: p.id }, p.name))) as HTMLSelectElement;
    const addBtn = h('button', { class: 'btn btn-ghost btn-sm' }, 'Add');
    addBtn.addEventListener('click', async () => {
      const profileId = addSelect.value;
      addBtn.setAttribute('disabled', 'true');
      try {
        const created = await api.addSubscriberToProfile(s.subscriber_id || '', profileId);
        // Reflect the new subscription locally: it appears in the list and
        // drops out of the dropdown — no popup close, no full page reload needed.
        current = [...current, adaptCreated(created, s, profileId)];
        toast('Added to ' + nameOf(profileId), 'ok');
        rerender();
        bgReload();
      } catch (e) {
        addBtn.removeAttribute('disabled');
        const msg = (e as Error).message || '';
        toast(/conflict/i.test(msg) ? 'They already see that profile' : msg, 'warn');
      }
    });
    addSlot.replaceChildren(h('div', { class: 'row', style: 'gap:8px;align-items:center' }, addSelect, addBtn));
  };

  // REMOVE: list every subscription this person holds. Each is individually
  // removable EXCEPT when it's the only one left: removing their last profile
  // (revoking the person entirely) is only allowed from the main subscription
  // view, so here the last remaining profile shows no Remove link.
  const renderList = (): void => {
    const isLast = !canRemove(current);
    list.replaceChildren(...current.map((m) => {
      const badge = h('span', { class: 'badge' }, nameOf(m.profile_id));
      if (isLast) {
        return h('div', { class: 'row between', style: 'align-items:center;margin-top:6px' },
          badge,
          h('small', { class: 'muted' }, 'Remove from the Subscriptions view'));
      }
      const rm = rowAction('Remove', async () => {
        if (!confirmDialog(`Remove them from “${nameOf(m.profile_id)}”? Key rotates; their cached copy goes stale.`)) return;
        try {
          await api.revokeSubscriber(m.id);
          // Reflect the removal locally: the profile leaves the list and returns
          // to the Add dropdown. When one profile remains, its Remove link
          // disappears (isLast is recomputed on this rerender).
          current = current.filter((x) => x.id !== m.id);
          toast('Removed from ' + nameOf(m.profile_id), 'ok');
          rerender();
          bgReload();
        } catch (err) { toast((err as Error).message, 'danger'); }
      }, { variant: 'danger' });
      return h('div', { class: 'row between', style: 'align-items:center;margin-top:6px' },
        badge, rm);
    }));
  };

  const rerender = (): void => { renderAdd(); renderList(); };
  rerender();

  const card = h('div', { class: 'card', style: 'max-width:440px;width:100%', onclick: (e: Event) => e.stopPropagation() },
    h('h3', {}, s.subscriber?.username || s.subscriber?.display_name || 'Subscriber'),
    h('small', { class: 'muted' }, 'Choose which of your profiles this person can see.'),
    fieldBlock('Add to another profile', addSlot),
    fieldBlock('Profiles they currently see', list));

  // Close sits BELOW the white card, directly on the dark overlay (the original
  // design). btn-on-overlay keeps its label white/readable on that backdrop.
  const close = h('button', { class: 'btn btn-ghost btn-sm btn-on-overlay', style: 'margin-top:12px' }, 'Close');
  close.addEventListener('click', () => overlay.remove());

  const box = h('div', { style: 'max-width:440px;width:100%', onclick: (e: Event) => e.stopPropagation() },
    card,
    h('div', { style: 'text-align:center' }, close));
  overlay.append(box); document.body.append(overlay);
}

// adaptCreated builds a local Subscription for the newly-added profile so the
// popup can update in place. Prefers the server's returned object; falls back to
// synthesizing one from the known ids when the API returns little/nothing.
function adaptCreated(created: unknown, s: Subscription, profileId: string): Subscription {
  const c = (created && typeof created === 'object') ? created as Partial<Subscription> : {};
  return {
    ...c,
    id: c.id || `local-${profileId}-${Date.now()}`,
    profile_id: c.profile_id || profileId,
    subscriber_id: c.subscriber_id || s.subscriber_id,
    subscriber: c.subscriber || s.subscriber,
    status: c.status || 'active',
  } as Subscription;
}

export function fieldBlock(label: string, control: HTMLElement): HTMLElement {
  return h('div', { style: 'margin-top:14px' },
    h('label', { class: 'muted', style: 'display:block;margin-bottom:6px' }, h('small', {}, label)),
    control);
}

// profilesTheySeeField renders the shared, clickable "Profiles they see" block:
// the profile badges this person currently sees + an Edit link, both opening the
// SAME subscriber editor. Returns null when this person is not a subscriber to any
// of my profiles (nothing to show). Rendered INSIDE the contact card (mergedCard)
// so it appears identically on My Contacts and in every popup, and sits on the
// card surface (readable) rather than floating on the modal overlay.
export function profilesTheySeeField(
  subs: Subscription[],
  profiles: Profile[],
  reloadSubs: () => Promise<void>,
): HTMLElement | null {
  if (!subs.length || !profiles.length) return null;
  const nameOf = (id?: string) => profiles.find((p) => p.id === id)?.name || '—';
  const badges = subs.map((m) => h('span', { class: 'badge badge-brand', style: 'margin-right:6px' }, nameOf(m.profile_id)));
  const editLink = h('a', { href: '#', style: 'text-decoration:none' }, ' Edit');
  const openEditor = (e: Event): void => {
    e.preventDefault();
    openSubscriberEditor(subs[0], subs, profiles, async () => { await reloadSubs(); });
  };
  editLink.addEventListener('click', openEditor);
  const wrap = h('div', { style: 'margin-top:12px;cursor:pointer' },
    h('small', { class: 'muted', style: 'display:block;margin-bottom:6px' }, 'Profiles they see'),
    h('div', {}, ...badges, editLink));
  wrap.addEventListener('click', openEditor);
  return wrap;
}
